<template>
  <templates-contentful-content
    v-if="post"
    v-bind="propsData"
    template="ArticleWithSidebarV2"
  >
    <template #main-content>
      <p v-if="post.timeToRead">
        <strong>Time to read : {{ post.timeToRead }} Minutes</strong>
      </p>

      <RichTextRenderer
        :document="post.mainContent.json"
        :node-renderers="renderNodes()"
      />

      <hr class="my-5 border-b border-b-gray-400" />

      <ab-test-wrapper
        v-if="_slug === 'have-you-become-a-mortgage-prisoner'"
        css-class="ab-test-refinance-calculator show-a mb-6"
      >
        <template #component_b>
          <MoleculesCalculatorWithDisclaimer calculator="refinance" />
        </template>
      </ab-test-wrapper>

      <MoleculesShareButtons
        class="mb-9"
        title="Share this article"
        :buttons="[
          {
            name: 'Facebook',
            url: `https://www.facebook.com/sharer/sharer.php?u=${articleLink}`,
          },
          {
            name: 'Twitter',
            url: `https://twitter.com/share?text=${title}&url=${articleLink}`
          },
          {
            name: 'Linkedin',
            url: `https://www.linkedin.com/shareArticle?mini=true&url=${articleLink}`,
          },
          {
            name: 'Whatsapp',
            url: `https://api.whatsapp.com/send?text=${articleLink}`,
          },
          {
            name: 'Copy',
            url: articleLink,
          },
        ]"
      />
    </template>

    <template #sidebar-right>
      <MoleculesPostTileGroup
        :items="latestNews"
        title="Latest news"
        type="sidebar"
        layout="small-tile"
        has-divider
      />

      <MoleculesExpertAnalysisSubscribeForm />
    </template>

    <template #footer>
      <div class="bg-gray-10 px-4 xl:px-0 pt-6 pb-[2.625rem] md:pt-10 md:pb-12">
        <div class="w-full max-w-wrapper-lg mx-auto">
          <atoms-headline class="text-lg font-bold mb-5">
            About the author
          </atoms-headline>

          <organisms-author-widget
            class="max-w-wrapper-sm mb-5"
            :author-name="author?.name"
            :author-image="author?.image?.url"
            :author-url="author?.url"
            :job-title="author?.jobTitle"
            :social-media="author?.socialMedia"
          >
            <p>
              {{ author?.authorProfile }}
            </p>
          </organisms-author-widget>

          <hr class="mb-4 border-b border-b-gray-400" />

          <template v-if="!_isEmpty(authorPosts)">
            <p class="text-lg font-bold mb-4">
              Recent articles by this author
            </p>

            <MoleculesPostTileGroup
              :items="authorPosts?.items"
              :items-per-row="4"
              image-lazy-load
            />
          </template>
        </div>
      </div>

      <div class="pt-10 px-4 xl:px-0 pb-[2.625rem] md:pb-12">
        <MoleculesPostTileGroup
          class="w-full max-w-wrapper-lg mx-auto"
          title="Related News"
          title-size="large"
          :items="relatedPosts?.items"
          :items-per-row="4"
          :has-load-more="relatedPosts.items.length !== relatedPosts.total"
          image-lazy-load
          @load-more="handleLoadMore"
        />
      </div>
    </template>
  </templates-contentful-content>
</template>

<script setup>
import _isEmpty from 'underscore/cjs/isEmpty.js'

import dayjs from 'dayjs'
import RichTextRenderer from 'contentful-rich-text-vue-renderer'

import ExpertAnalysisService from '~/server/contentful/expert-analysis/service'

import {
  RENDER_NODES,
  GET_FEATURE_IMAGE
} from '~/helpers/contentful'

import {
  EXPERT_EASE_AUTHOR_URL,
  EXPERT_EASE_LOAD_MORE_PER_PAGE
} from '~/constants/pages/contentful'

defineOptions({
  name: 'TemplatesContentfulExpertAnalysis'
})

const props = defineProps({
  isPreview: {
    type: Boolean,
    default: false
  }
})

const {
  internalDomainForInlinesHyperlink
} = useEnv()

const {
  getPostsByAuthor,
  getRelatedPosts
} = new ExpertAnalysisService()

const { $bus } = useNuxtApp()
const { devices } = deviceChecker()
const $route = useRoute()

const _category = $route.params.category
const _slug = $route.params.slug

const path = `/expert-analysis/${_category}/${_slug}`
const articleLink = import.meta.client ? window.location.href : ''

const { post, latestNews } = await useFetch('/api/contentful/expert-analysis/get-post', {
  method: 'post',
  body: {
    slug: path,
    preview: props.isPreview
  }
}).then(response => { 
  if (!_isEmpty(response?.error?.value?.data)) { 
    throw(response?.error?.value?.data)
  }

  const article = response?.data?.value || undefined

  if (_isEmpty(article)) {
    throw createError({
      statusCode: 404,
      statusMessage: 'POST DOES NOT EXIST',
      fatal: true
    })
  }

  return {
    post: article?.post,
    latestNews: article?.latestNews
  }
}).catch(err => {
  throw createError(err)
})

const title = (() => {
  if (
    post?.titleRichText &&
    typeof post?.titleRichText !== 'undefined'
  ) {
    const parse = documentToHtmlString(post?.titleRichText?.json)

    if (removeHtmlString(parse) !== '') {
      return parse.replace(/&amp;/g, '&')
    }
  }

  return post.title
})()

const author = computed(() => {
  const temp = post?.author

  return {
    name: temp?.name ?? 'Compare Club',
    image: {
      url: temp?.authorImage?.url ?? FEATURED_IMAGE_DEFAULT_IMAGE
    },
    url: `${EXPERT_EASE_AUTHOR_URL}${temp.authorUrl}/`,
    jobTitle: temp?.authorJobTitle,
    authorProfile: temp?.authorProfile,
    sys: {
      id: temp?.sys?.id
    },
    socialMedia: [
      (temp?.twitter && {
        name: 'Twitter',
        url: temp?.twitter
      }) || false,
      (temp?.linkedin && {
        name: 'Linkedin',
        url: temp?.linkedin
      }) || false,
      (temp?.email && {
        name: 'Email',
        url: `mailto:${temp?.email}`
      }) || false
    ].filter(item => item)
  }
})

const tags = computed(() => {
  const temp = post?.contentfulMetadata?.tags

  return !_isEmpty(temp)
    ? temp.map(tag => tag.id)
    : temp
})

const propsData = computed(() => {
  return {
    title: post.title,
    lastUpdate: post?.date ?? post.sys?.publishedAt,
    featuredImage: GET_FEATURE_IMAGE(
      post.featureImage,
      'ArticleWithSidebarV2',
      devices.currBreakpoint.value
    ),
    hasShareButtons: true,
    postType: 'Expert Analysis',
    author: author.value,
    tags: post?.contentfulMetadata?.tags,
    breadcrumbs: [
      {
        text: 'Home',
        name: 'Home',
        url: '/'
      },
      {
        text: 'Expert Analysis',
        name: 'Everyday ExpertEase',
        url: '/expert-analysis/'
      },
      {
        text: post.category,
        name: post.category,
        url: `/expert-analysis/${_category}`
      },
      {
        text: removeHtmlString(title),
        url: `${post.slug}/`,
        name: removeHtmlString(title),
        active: true
      }
    ]
  }
})

const relatedPostsPageConfig = ref()

const { relatedPosts, authorPosts } = await (async () => {
  const [
    relatedPostsData,
    authorPostsData
  ] = await Promise.all([
    getRelatedPosts({
      tags: tags.value,
      slug: path,
      category: post.category
    }),
    getPostsByAuthor({
      author: post.author.name,
      slug: path
    })
  ])

  relatedPostsPageConfig.value = {
    limit: 4,
    total: relatedPostsData.total
  }

  return {
    relatedPosts: reactive(relatedPostsData),
    authorPosts: authorPostsData
  }
})()

const noCanonicalLink = !!post?.noCanonicalLink
const noIndex = props.isPreview
  ? false
  : !!post?.noIndex
const canonicalLink = post?.canonicalLink || `${CC_DOMAIN}${post?.slug}/`

const entriesMap = (() => {
  const map = new Map()

  if (_isEmpty(post)) {
    return map
  }

  const links = post.mainContent?.links

  for (const entry of links.entries.block) {
    map.set(entry.sys.id, entry)
  }

  return map
})()

const assetsMap = (() => {
  const map = new Map()

  if (_isEmpty(post)) {
    return map
  }

  const links = post.mainContent?.links

  for (const assets of links.assets.block) {
    map.set(assets.sys.id, assets)
  }

  return map
})()

const renderNodes = () => RENDER_NODES({
  entriesMap,
  assetsMap,
  internalDomainForInlinesHyperlink,
  author: post?.author,
  inlineComponents: {
    CTA: resolveComponent('AtomsCta'),
    MoleculesCalculatorWithDisclaimer: resolveComponent('MoleculesCalculatorWithDisclaimer'),
    KeyPoints: resolveComponent('MoleculesKeyPoints'),
    InlineAge: resolveComponent('MoleculesInlineAge'),
    InlineCardCta: resolveComponent('MoleculesInlineCardCta'),
    InlineLifeStage: resolveComponent('MoleculesInlineLifeStage'),
    InlinePartner: resolveComponent('MoleculesInlinePartner'),
    VerticalFormWidget: resolveComponent('MoleculesVerticalFormWidgetSidebar'),

    MoleculesInlineLifeStage: resolveComponent('MoleculesInlineLifeStage'),
    Lifestage: resolveComponent('OrganismsInlineLifeStageAbTest'),
    State: resolveComponent('MoleculesInlineCardCta'),
    InlineAgeAbTest: resolveComponent('OrganismsInlineAgeAbTest'),
    AgeRange: resolveComponent('MoleculesInlineCardCta'),
    Partner: resolveComponent('MoleculesInlinePartner'),

    AUTHORBLOCK: resolveComponent('AtomsAuthorBlock'),
    FaqItem: resolveComponent('AtomsAccordion'),

    VideoAsset: resolveComponent('AtomsVideoFrame')
  }
})

async function handleLoadMore () {
  const offset = relatedPostsPageConfig.value?.limit
  const total = relatedPostsPageConfig.value?.total

  await getRelatedPosts({
    tags: tags.value,
    slug: path,
    category: post?.category,
    limit: EXPERT_EASE_LOAD_MORE_PER_PAGE,
    offset
  }).then(res => {
    const data = JSON.parse(JSON.stringify(res))
    const items = data?.items

    if (!_isEmpty(items)) {
      if (isObject(items)) {
        relatedPosts.items.push(items)
      } else {
        relatedPosts.items.push(...items)
      }
    }

    const newOffset = offset + EXPERT_EASE_LOAD_MORE_PER_PAGE

    relatedPostsPageConfig.value.limit = newOffset >= total
      ? total
      : newOffset

    return res
  }).catch(error => {
    throw new Error(error)
  })
}

const postDate = post?.sys?.firstPublishedAt ?? ''

$bus.emit('pageview', {
  pageCategory: post?.category ?? 'category',
  pageVertical: post?.vertical ?? 'vertical',
  pagePostAuthor: author.value?.name ?? 'Compare Club',
  pagePostDateIso: postDate,
  pagePostDate: postDate
    ? dayjs(postDate).format('DD MMMM YYYY')
    : '',
  pagePostDateYear: postDate
    ? dayjs(postDate).format('YYYY')
    : '',
  pagePostDateMonth: postDate
    ? dayjs(postDate).format('MM')
    : '',
  pagePostDateDay: postDate
    ? dayjs(postDate).format('DD')
    : '',
  pagePostDateDayName: postDate
    ? dayjs(postDate).format('dddd')
    : '',
  pagePostDateHour: postDate
    ? dayjs(postDate).format('HH')
    : '',
  pagePostDateMinute: postDate
    ? dayjs(postDate).format('MM')
    : ''
})

generateMeta({
  title: removeHtmlString(title) || 'Compare Club',
  ogTitle: post?.seoTitle ?? removeHtmlString(title) ?? 'Compare Club',

  description: post?.metaDescription ?? '',
  ogDescription: post?.metaDescription ?? '',

  canonical: noCanonicalLink ? '' : canonicalLink,
  ogUrl: canonicalLink,
  ogImage: propsData.value.featuredImage?.url || FEATURED_IMAGE_DEFAULT_IMAGE,
  ogType: 'product',
  meta: [
    {
      itemprop: 'mainEntityOfPage',
      content: canonicalLink
    },
    {
      itemprop: 'dateModified',
      content: post.sys.publishedAt
    }
  ],
  noIndex,
  noFollow: noIndex
})

useJsonld(() => {
  if (props.isPreview) {
    return {}
  }

  const imageDetails = {
    url: post.featureImage?.url || FEATURED_IMAGE_DEFAULT_IMAGE,
    desc: post.featureImage?.description
  }

  const postUrl = `${CC_DOMAIN}${post.slug}`
  const authorId = `${CC_DOMAIN}/#schema/person/${author.value?.sys.id}`

  const mainContent = post?.mainContent
    ? removeHtmlString(documentToHtmlString(post.mainContent?.json)).split(' ')
    : []

  const organization = {
    '@type': 'Organization',
    '@id': `${CC_DOMAIN}/#organization`,
    name: 'CompareClub',
    url: CC_DOMAIN,
    sameAs: [
      'https://www.instagram.com/compareclub_au/',
      'https://au.linkedin.com/company/compare-club',
      'https://www.facebook.com/compareclubau/',
      'https://twitter.com/CompareClub'
    ],
    logo: {
      '@type': 'ImageObject',
      inLanguage: 'en-US',
      '@id': `${CC_DOMAIN}/#/schema/logo/image/`,
      url: 'https://asset.compareclub.com.au/edge/static/images/cc-logo.svg',
      contentUrl: 'https://asset.compareclub.com.au/edge/static/images/cc-logo.svg',
      width: 190,
      height: 32,
      caption: 'CompareClub'
    },
    image: {
      '@id': `${CC_DOMAIN}/#/schema/logo/image/`
    }
  }

  const website = {
    '@type': 'WebSite',
    '@id': `${CC_DOMAIN}/#website`,
    url: CC_DOMAIN,
    name: 'CompareClub',
    description: 'Do your household bills need saving? Compare, change and save money on your health insurance, energy bills, home loans and life cover.',
    publisher: {
      '@id': `${CC_DOMAIN}/#organization`
    },
    inLanguage: 'en-US'
  }

  const imageObject = {
    '@type': 'ImageObject',
    inLanguage: 'en-US',
    '@id': `${postUrl}/#primaryimage`,
    url: imageDetails?.url,
    contentUrl: imageDetails?.url,
    width: 736,
    height: 340,
    caption: imageDetails?.desc
  }

  const webPage = {
    '@type': 'WebPage',
    '@id': `${postUrl}/#webpage`,
    url: postUrl,
    name: title,
    isPartOf: { '@id': `${CC_DOMAIN}/#website` },
    primaryImageOfPage: {
      '@id': `${postUrl}/#primaryimage`
    },
    datePublished: post.sys.firstPublishedAt,
    dateModified: post.sys.publishedAt,
    description: post.metaDescription,
    breadcrumb: {
      '@id': `${postUrl}/#breadcrumb`
    },
    inLanguage: 'en-US',
    potentialAction: [
      {
        '@type': 'ReadAction',
        target: [
          postUrl
        ]
      }
    ]
  }

  const breadcrumbList = {
    '@type': 'BreadcrumbList',
    '@id': `${postUrl}/#breadcrumb`,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: CC_DOMAIN
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Everyday ExpertEase',
        item: `${CC_DOMAIN}/expert-analysis/`
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: post.category,
        item: `${CC_DOMAIN}/expert-analysis/${_category}`
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: title,
        item: postUrl
      }
    ]
  }

  const newsArticle = {
    '@type': 'NewsArticle',
    '@id': `${postUrl}/#article`,
    isPartOf: {
      '@id': `${postUrl}/#webpage`
    },
    author: {
      '@type': 'Person',
      name: author.value?.name,
      '@id': authorId,
      url: `${CC_DOMAIN}${author.value?.url}`
    },
    headline: title,
    datePublished: post.sys.firstPublishedAt,
    dateModified: post.sys.publishedAt,
    mainEntityOfPage: {
      '@id': `${postUrl}/#webpage`
    },
    wordCount: mainContent.length,
    publisher: {
      '@id': `${CC_DOMAIN}/#organization`
    },
    image: {
      '@id': `${postUrl}/#primaryimage`
    },
    thumbnailUrl: imageDetails?.url,
    keywords: tags.value,
    articleSection: [post?.category ?? post?.vertical],
    inLanguage: 'en-US'
  }

  const person = {
    '@type': 'Person',
    '@id': authorId,
    name: author.value?.name,
    image: {
      '@type': 'ImageObject',
      inLanguage: 'en-US',
      '@id': `${CC_DOMAIN}/#/schema/person/image/`,
      url: author.value?.image?.url,
      contentUrl: author.value?.image?.url,
      caption: author.value?.name
    },
    url: `${CC_DOMAIN}${author.value?.url}`
  }

  const faqList = !_isEmpty(post?.faqSchema) && props?.hideFaqSchema
    ? post?.faqSchema
    : undefined

  const faqSchema = (faqList && {
    '@type': 'FAQPage',
    mainEntity: faqList.map(item => ({
      '@type': 'Question',
      name: item?.key ?? '',
      acceptedAnswer: {
        '@type': 'Answer',
        text: item?.value ?? ''
      }
    }))
  }) || false

  return {
    '@context': 'http://schema.org',
    '@graph': [
      organization,
      website,
      imageObject,
      webPage,
      breadcrumbList,
      newsArticle,
      person,
      faqSchema
    ].filter(item => item)
  }
})
</script>
